// ARScene.jsx
import { Canvas } from "@react-three/fiber";
import { XR, XRDomOverlay, createXRStore } from "@react-three/xr";
import XrHit from "./XrHit";
import { Suspense, useState, useRef, useCallback, useEffect } from "react";
import {
  Environment,
  Html,
  OrbitControls,
  ContactShadows,
  useProgress,
} from "@react-three/drei";
import GateColorPicker from "./components/GateColorPicker";
import GateSwitcher from "./components/GateSwitcher";
import WicketToggle from "./components/WicketToggle";
import stl from "./XRScene.module.css";
import {
  DEFAULT_USDZ,
  isQuickLookSupported,
  openQuickLook,
  resolveUsdzUrl,
} from "./utils/arQuickLook";

const store = createXRStore({
  requiredFeatures: ["hit-test"],
  optionalFeatures: ["dom-overlay", "local-floor"],
});

// Выносим Loader в отдельный компонент
function ModelLoader() {
  const { progress } = useProgress();

  return (
    <Html center>
      <div
        style={{
          width: "300px",
          textAlign: "center",
          color: "white",
          fontFamily: "Montserrat, sans-serif",
          background: "rgba(0,0,0,0.8)",
          padding: "20px",
          borderRadius: "10px",
        }}>
        <div
          style={{
            height: "20px",
            width: "100%",
            background: "rgba(255,255,255,0.2)",
            borderRadius: "10px",
            overflow: "hidden",
            marginBottom: "10px",
          }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: "#ef8b00",
              transition: "width 0.2s ease",
            }}
          />
        </div>
        <span>Загрузка модели: {progress.toFixed(0)}%</span>
      </div>
    </Html>
  );
}

const ARScene = () => {
  const [modelParams, setModelParams] = useState(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [arError, setArError] = useState(null);

  // false = ворота закрыты (дефолт) → активна кнопка open
  // true  = ворота открыты          → активна кнопка close
  const [gateOpen, setGateOpen] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [modelPlaced, setModelPlaced] = useState(false);

  const gateControlRef = useRef(null);
  const finishTimerRef = useRef(null);

  const clearFinishTimer = () => {
    if (finishTimerRef.current) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
  };

  useEffect(() => clearFinishTimer, []);

  // Идёт ли AR-сессия (снаружи Canvas хук useXR недоступен — подписываемся на store)
  const [inAR, setInAR] = useState(() => !!store.getState().session);
  useEffect(() => store.subscribe((state) => setInAR(!!state.session)), []);

  // После выхода из AR модель снова не «поставлена»
  useEffect(() => {
    if (!inAR) setModelPlaced(false);
  }, [inAR]);

  // === iOS: AR Quick Look с USDZ ===
  const [isIOSQuickLook] = useState(isQuickLookSupported);
  const [usdzUrl, setUsdzUrl] = useState(DEFAULT_USDZ);

  // Заранее подбираем usdz под выбранный цвет,
  // чтобы по клику открыть его синхронно (иначе Safari может не открыть AR)
  useEffect(() => {
    if (!isIOSQuickLook) return;
    let cancelled = false;
    resolveUsdzUrl(modelParams?.activeMaterial).then((url) => {
      if (!cancelled) setUsdzUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [isIOSQuickLook, modelParams?.activeMaterial]);

  // Срабатывает после завершения анимации (событие mixer "finished")
  const handleAnimationFinished = useCallback((isOpen) => {
    clearFinishTimer();
    setGateOpen(isOpen);
    setAnimating(false);
  }, []);

  const handleModelPlacedChange = useCallback((placed) => {
    setModelPlaced(placed);
  }, []);

  const handleToggleGate = () => {
    if (animating) return;
    const controller = gateControlRef.current;
    if (!controller) return;

    const next = !gateOpen;
    setAnimating(true);
    const duration = controller.toggleGate(next) || 0;

    // Страховка: если событие "finished" не придёт — не блокируем кнопку навсегда
    clearFinishTimer();
    finishTimerRef.current = setTimeout(
      () => {
        setGateOpen(next);
        setAnimating(false);
      },
      (duration > 0 ? duration * 1000 : 6000) + 800,
    );
  };

  // Функция для смены активных ворот
  const setActiveGate = (gateName) => {
    if (!modelParams) return;
    setModelParams({
      ...modelParams,
      activeGate: gateName,
    });
  };

  // Функция для обработки загрузки модели
  const handleModelLoad = () => {
    setIsModelLoaded(true);
  };

  const handleEnterAR = async () => {
    // iPhone/iPad: WebXR нет — открываем USDZ в AR Quick Look
    if (isIOSQuickLook) {
      setArError(null);
      openQuickLook(usdzUrl);
      return;
    }

    try {
      setArError(null);
      if (
        navigator.xr &&
        (await navigator.xr.isSessionSupported("immersive-ar"))
      ) {
        await store.enterAR();
      } else {
        setArError("Ваше устройство или браузер не поддерживает AR");
      }
    } catch (err) {
      console.error("Ошибка запуска AR:", err);
      setArError("Не удалось запустить AR-сессию: " + err.message);
    }
  };

  return (
    <div
      className={stl.root}
      style={{
        // dvh — видимая высота экрана с учётом адресной строки Android Chrome
        // (100vh там больше экрана, и низ страницы уезжает под панель браузера)
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
      {/* Кнопка всегда видна поверх всего */}
      <button onClick={handleEnterAR} className={stl.enterBtn}>
        Примерить ворота
      </button>

      {/* Сообщение об ошибке */}
      {arError && <div className={stl.error}>{arError}</div>}

      {/* 3D занимает всё место между кнопкой и панелью */}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <Canvas camera={{ position: [2.5, 1.4, 6], fov: 40 }}>
          <XR store={store}>
            {/* Вращение/зум мышью и пальцами — только вне AR */}
            {!inAR && (
              <>
                <OrbitControls
                  makeDefault
                  enablePan={false}
                  // не даём смотреть сверху (вид «сверху» и был на Android)
                  minPolarAngle={Math.PI / 3}
                  maxPolarAngle={Math.PI / 2 - 0.05}
                />
                <ContactShadows
                  position={[0, 0, 0]}
                  opacity={0.4}
                  scale={10}
                  blur={2}
                  far={4}
                />
              </>
            )}
            <Suspense fallback={<ModelLoader />}>
              <Environment preset="sunset" background={false} />
              <XrHit
                setModelParams={setModelParams}
                modelParams={modelParams}
                onModelLoaded={handleModelLoad}
                gateControlRef={gateControlRef}
                onGateAnimationFinished={handleAnimationFinished}
                onModelPlacedChange={handleModelPlacedChange}
              />

              {/* Интерфейс управления показываем только после загрузки модели */}
              {isModelLoaded && (
                <XRDomOverlay className={stl.overlay}>
                  <div className={stl.bottomStack}>
                    {/* Одна кнопка — меняется после завершения анимации */}
                    <div className={stl.lockButtons}>
                      <button
                        onClick={handleToggleGate}
                        disabled={animating || !modelPlaced}
                        className={stl.lockBtn}
                        aria-label={
                          gateOpen ? "Закрыть ворота" : "Открыть ворота"
                        }>
                        <img
                          className={stl.lockIcon}
                          src={
                            gateOpen ? "/models/close.png" : "/models/open.png"
                          }
                          alt={gateOpen ? "Закрыть" : "Открыть"}
                          draggable={false}
                        />
                      </button>
                    </div>

                    {/* Стеклянная панель */}
                    <div className={stl.controls}>
                      <div>
                        <GateColorPicker
                          modelParams={modelParams}
                          setModelParams={setModelParams}
                        />
                        {/* <GateSwitcher
                        modelParams={modelParams}
                        setActiveGate={setActiveGate}
                      /> */}
                        <WicketToggle modelParams={modelParams} />
                      </div>

                      {/* Первый ряд: перемещение X/Z и удаление */}
                      <div className={stl.row}>
                        <button id="move-left" className={stl.btn}>
                          ←
                        </button>
                        <button id="move-forward" className={stl.btn}>
                          ↑
                        </button>
                        <button id="move-backward" className={stl.btn}>
                          ↓
                        </button>
                        <button id="move-right" className={stl.btn}>
                          →
                        </button>
                        <button
                          id="remove-cube"
                          className={`${stl.btn} ${stl.danger}`}
                          aria-label="Удалить модель"
                          title="Удалить">
                          ✕
                        </button>
                      </div>

                      {/* Второй ряд: перемещение Y, поворот и масштаб */}
                      <div className={stl.row}>
                        <button
                          id="move-up"
                          className={`${stl.btn} ${stl.success}`}>
                          ↗️
                        </button>
                        <button
                          id="move-down"
                          className={`${stl.btn} ${stl.warning}`}>
                          ↘️
                        </button>
                        <button
                          id="rotate-ccw"
                          className={`${stl.btn} ${stl.rotate}`}>
                          ↺
                        </button>
                        <button
                          id="rotate-cw"
                          className={`${stl.btn} ${stl.rotate}`}>
                          ↻
                        </button>
                        <button
                          id="scale-down"
                          className={`${stl.btn} ${stl.scale}`}>
                          ─
                        </button>
                        <button
                          id="scale-up"
                          className={`${stl.btn} ${stl.scale}`}>
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </XRDomOverlay>
              )}
            </Suspense>
          </XR>
        </Canvas>
      </div>

      {/* Стартовая страница: настройки до входа в AR, в обычном потоке под 3D
          (на iOS это же место выбора цвета перед Quick Look) */}
      {!inAR && isModelLoaded && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "0 12px",
            marginBottom: "max(12px, env(safe-area-inset-bottom))",
            padding: "8px 12px",
            borderRadius: 12,
            background: "rgba(0,0,0,0.55)",
            color: "white",
          }}>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
            }}>
            <GateColorPicker
              compact
              modelParams={modelParams}
              setModelParams={setModelParams}
            />
            <WicketToggle modelParams={modelParams} />
          </div>
          <button
            onClick={handleToggleGate}
            disabled={animating}
            className={stl.lockBtn}
            style={{ position: "static", flexShrink: 0 }}
            aria-label={gateOpen ? "Закрыть ворота" : "Открыть ворота"}>
            <img
              className={stl.lockIcon}
              src={gateOpen ? "/models/close.png" : "/models/open.png"}
              alt={gateOpen ? "Закрыть" : "Открыть"}
              draggable={false}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default ARScene;
